// 52-week cadence strip. One row × 52 cells. Fed by rock updated_at +
// MonthlyCheckIn.completed_at timestamps, bucketed by ISO week (Monday).
//
// Props:
//   cells: [{ weekStart: "YYYY-MM-DD", intensity: 0|1|2, rockUpdates: N, monthlyCheckin: bool }]
//   readOnly: disables click navigation in the demo (not wired yet).

const INTENSITY_FILL = {
  0: "fill-zinc-100",
  1: "fill-kf-red/30",
  2: "fill-kf-red",
}

const CELL_W = 14
const CELL_H = 24
const GAP = 2

function monthLabel(weekStart) {
  const d = new Date(weekStart)
  return d.toLocaleString("en-US", { month: "short" })
}

function tooltip(cell) {
  const parts = []
  if (cell.rockUpdates) parts.push(`${cell.rockUpdates} rock update${cell.rockUpdates === 1 ? "" : "s"}`)
  if (cell.monthlyCheckin) parts.push("monthly check-in")
  const body = parts.length > 0 ? parts.join(" + ") : "no activity"
  return `Week of ${cell.weekStart} — ${body}`
}

export function CadenceHeatmap({ cells = [] }) {
  if (cells.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-zinc-300 p-6 text-center">
        <p className="text-sm text-zinc-500">No activity yet.</p>
      </div>
    )
  }

  const currentIdx = cells.length - 1
  const width = cells.length * (CELL_W + GAP)
  const height = CELL_H + 20 // cells + month-label row

  // Find month-boundary indices (first week of each new month)
  const monthBoundaries = []
  let lastMonth = null
  cells.forEach((cell, i) => {
    const m = new Date(cell.weekStart).getMonth()
    if (m !== lastMonth) {
      monthBoundaries.push({ idx: i, label: monthLabel(cell.weekStart) })
      lastMonth = m
    }
  })

  return (
    <div>
      <div className="flex items-baseline justify-between gap-3 mb-1">
        <h3 className="font-display text-lg text-zinc-900">Cadence</h3>
        <p className="text-xs text-zinc-500 shrink-0">Last 52 weeks</p>
      </div>
      <p className="text-sm text-zinc-600 mb-3 max-w-prose">
        Each square is one week. Color shows how often you touched your plan—rock
        progress updates and monthly check-ins add up to a fuller week.
      </p>
      <div className="overflow-x-auto">
        <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className="block">
          {cells.map((cell, i) => {
            const x = i * (CELL_W + GAP)
            const isCurrent = i === currentIdx
            return (
              <g key={i}>
                <rect
                  x={x}
                  y={0}
                  width={CELL_W}
                  height={CELL_H}
                  rx={3}
                  className={`${INTENSITY_FILL[cell.intensity]} ${isCurrent ? "animate-pulse" : ""}`}
                  stroke={isCurrent ? "#c1272f" : "transparent"}
                  strokeWidth={isCurrent ? 1.5 : 0}
                >
                  <title>{tooltip(cell)}</title>
                </rect>
              </g>
            )
          })}
          {monthBoundaries.map(({ idx, label }) => (
            <text
              key={idx}
              x={idx * (CELL_W + GAP)}
              y={CELL_H + 14}
              fontSize="10"
              className="fill-zinc-500"
            >
              {label}
            </text>
          ))}
        </svg>
      </div>
      <div className="flex items-center gap-4 mt-3 text-[11px] text-zinc-500">
        <span className="inline-flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-sm bg-zinc-100" /> empty
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-sm bg-kf-red/30" /> one update
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-sm bg-kf-red" /> active week
        </span>
      </div>
    </div>
  )
}
